import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadCustomReport,
  downloadReport,
} from "../store/actions/reportActions";
import {
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { fetchUsages } from "../store/actions/billingActions";
import DownloadIcon from "@mui/icons-material/Download";
import GlobalMessage from "../components/GlobalMessage";
import { useTranslation } from "react-i18next";
import LinearProgress from "@mui/material/LinearProgress";
import ServiceStatus from "../components/ServiceStatus";

// The monthly usage table below is legacy: it no longer works and the data
// behind it is stale. Hidden rather than deleted so the markup and the
// download wiring survive until there is a replacement. Flip to true to bring
// it back; the fetch is skipped while it is off so we are not calling the
// usages API for something nobody sees.
const SHOW_LEGACY_USAGE_TABLE = false;


const Dashboard = () => {
  const now = new Date();
  const dispatch = useDispatch();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [downloading, setDownloading] = useState({}); // Track downloading states per row
  const [customDownloading, setCustomDownloading] = useState(false); // Track downloading states per row
  const [startingFrom, setStartingFrom] = useState(null); // Date pickers
  const [upUntil, setUpUntil] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { t } = useTranslation(); // Get the t function

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingTimerRef = useRef(null);

  // Retrieve data from Redux store
  const usages = useSelector((state) => state.billing.usages);

  useEffect(() => {
    if (!SHOW_LEGACY_USAGE_TABLE) return;
    setLoading(true);
    setLoadingProgress(0);

    // Simulate progress bar, finish in 3 sec or earlier if fetch finishes
    const duration = 1000;
    const interval = 100;
    const start = Date.now();
    loadingTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      let percent = Math.min(100, (elapsed / duration) * 100);
      setLoadingProgress(percent);
    }, interval);

    dispatch(fetchUsages({ month, year }))
      .unwrap()
      .catch((error) => {
        setFetchError(t("notifications.failedToFetch"));
        setOpenSnackbar(true);
      })
      .finally(() => {
        setLoadingProgress(100);
        setTimeout(() => setLoading(false), 300); // allow bar to finish
        clearInterval(loadingTimerRef.current);
      });
    return () => clearInterval(loadingTimerRef.current);
  }, [dispatch, month, year]);

  const handleDownload = (id, month, year) => {
    setDownloading((prev) => ({ ...prev, [id]: true })); // Set downloading state for the specific row

    dispatch(downloadReport({ month, year }))
      .then(() => {
        // Handle success logic if needed
        setSuccessMessage(t("notifications.downloadStarted"));
        setOpenSnackbar(true);
      })
      .catch((error) => {
        setFetchError(t("notifications.downloadFailed"));
        setOpenSnackbar(true);
        // Handle error logic if needed
        console.error(`Download failed for ID: ${id}`, error);
      })
      .finally(() => {
        setDownloading((prev) => ({ ...prev, [id]: false }));
      });
  };

  const handleCustomDownload = () => {
    if (!startingFrom) {
      setFetchError("Please select at least the start date.");
      setOpenSnackbar(true);
      return;
    }

    setCustomDownloading(true);

    dispatch(downloadCustomReport({ fromDate: startingFrom, toDate: upUntil }))
      .then(() => {
        setSuccessMessage("Download started");
        setOpenSnackbar(true);
        setCustomDownloading(false);
      })
      .catch((error) => {
        setFetchError("Download failed");
        setOpenSnackbar(true);
        setCustomDownloading(false);

        console.error(`Download failed for custom report`, error);
      });
  };

  const handleCloseSnackbar = () => {
    setFetchError("");
    setSuccessMessage("");
    setOpenSnackbar(false);
  };

  const maskAsCurrency = (value) => {
    const options = {
      style: "currency",
      currency: "EUR",
    };
    if (value < 0.01) {
      // Show many decimal places for small values
      options.minimumFractionDigits = 4;
      options.maximumFractionDigits = 6;
    } else {
      // Show 2 decimal places for values >= 0.01
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
    }
    const formatter = new Intl.NumberFormat("de-DE", options);
    return formatter.format(value);
  };

  return (
    <Box p={3}>
      <ServiceStatus />

      {SHOW_LEGACY_USAGE_TABLE && (
      <Paper elevation={3} sx={{ padding: 2, marginBottom: 4 }}>
        <div style={{ paddingTop: "10px" }}>
          {/* Filters */}
          <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
            <Select value={month} onChange={e => setMonth(Number(e.target.value))}>
              {[...Array(12)].map((_, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{idx + 1}</MenuItem>
              ))}
            </Select>

            <Select value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2023, 2024, 2025].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </div>

          {/* Table or Empty State */}
          {usages.length === 0 && loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} width="100%">
              <LinearProgress variant="determinate" value={loadingProgress} style={{ width: 300, height: 8, borderRadius: 4 }} />
              <Typography variant="h6" mt={3} color="textSecondary" align="center">
                {t("dashboard.loadingState")}
              </Typography>
            </Box>
          ) : usages.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
              {/* Simple SVG artwork */}
              <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="60" cy="60" r="56" fill="#F5F5F5" stroke="#E0E0E0" strokeWidth="4" />
                <rect x="35" y="55" width="50" height="30" rx="6" fill="#E0E7FF" />
                <rect x="45" y="65" width="30" height="6" rx="3" fill="#C7D2FE" />
                <rect x="45" y="75" width="18" height="6" rx="3" fill="#C7D2FE" />
                <circle cx="60" cy="48" r="8" fill="#A5B4FC" />
              </svg>
              <Typography variant="h6" mt={3} color="textSecondary" align="center">
                {t("dashboard.emptyState")}
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("dashboard.month")}</TableCell>
                  <TableCell>{t("dashboard.year")}</TableCell>
                  <TableCell>{t("dashboard.periodStart")}</TableCell>
                  <TableCell>{t("dashboard.periodEnd")}</TableCell>
                  <TableCell>{t("dashboard.sendNotDelivered")}</TableCell>
                  <TableCell>{t("dashboard.deliveredSms")}</TableCell>
                  <TableCell>{t("dashboard.deliveredViberMessages")}</TableCell>
                  <TableCell>{t("dashboard.spentCredit")}</TableCell>
                  <TableCell>{t("dashboard.totalCredits")}</TableCell>
                  <TableCell>{t("dashboard.report")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usages.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.month}</TableCell>
                    <TableCell>{item.year}</TableCell>
                    <TableCell>
                      {new Date(item.periodStart).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(item.periodEnd).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {item.sentMessages}
                    </TableCell>
                    <TableCell>{item.deliveredSms}</TableCell>
                    <TableCell>{item.deliveredViberMessages}</TableCell>
                    <TableCell>
                      {maskAsCurrency(item.currentSpentCredits)}
                    </TableCell>
                    <TableCell>{maskAsCurrency(item.totalCredits)}</TableCell>
                    <TableCell>
                      {downloading[item.id] ? (
                        <CircularProgress size={24} />
                      ) : (
                        <IconButton onClick={() => handleDownload(item.id, item.month, item.year)}>
                          <DownloadIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

        </div>
      </Paper>
      )}

      {/* Global Message */}
      <GlobalMessage
        open={openSnackbar}
        message={successMessage || fetchError}
        severity={successMessage ? "success" : "error"}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default Dashboard;
